import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  AlertTriangle,
  Calendar as CalendarIcon,
  CalendarClock,
  Check,
  ChevronDown,
  ExternalLink,
  Globe,
  Loader2,
  RotateCcw,
  X,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import { User } from '@/services/types/user-types';
import {
  getIanaTimezone,
  getTimezoneAbbrev,
} from '@/utils/timezone';

import { ComboboxLabel, ComboboxOption } from '../../ui/catalyst/combobox';
import { ComboboxWithClear } from '../../ui/catalyst/combobox-with-clear';

// Frequent assignees for callbacks (same as AssignmentDropdown)
const FREQUENT_ASSIGNEE_NAMES = [
  'Lisbeth',
  'Denise Biggs',
  'Cole',
  'Michelle Pimienta',
  'Jacqueline',
  'Julie Lopez',
];

// Generate time slots in 15-minute intervals (7 AM – 8 PM)
const TIME_SLOTS: string[] = Array.from({ length: (20 - 7) * 4 + 1 }, (_, i) => {
  const h = 7 + Math.floor(i / 4);
  const m = (i % 4) * 15;
  const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const display = `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
  const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return `${value}|${display}`;
});

interface ScheduleCallbackPanelProps {
  /** Client's state for timezone inference */
  clientState: string;
  /** Current intake assignee — used as default for primary assignee */
  intakeAssignedTo: string | null;
  scheduledAt: string | null;
  scheduledAssigneeName: string | null;
  scheduledBackupAssigneeName: string | null;
  scheduledAssigneeId: string | null;
  scheduledBackupAssigneeId: string | null;
  googleCalendarEventId: string | null;
  googleCalendarEventLink: string | null;
  onSchedule: (data: {
    scheduled_at: string;
    scheduled_assignee_id: string;
    scheduled_backup_assignee_id?: string;
    skip_calendar?: boolean;
  }) => Promise<void>;
  onCancel: () => Promise<void>;
  disabled?: boolean;
}

const displayUserName = (user: User | null): string => {
  if (!user) return '';
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.first_name || user.username;
};

const ScheduleCallbackPanelComponent: React.FC<ScheduleCallbackPanelProps> = ({
  clientState,
  intakeAssignedTo,
  scheduledAt,
  scheduledAssigneeName,
  scheduledBackupAssigneeName,
  scheduledAssigneeId,
  scheduledBackupAssigneeId,
  googleCalendarEventId,
  googleCalendarEventLink,
  onSchedule,
  onCancel,
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(!!scheduledAt);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Client timezone (from their state)
  const clientTzIana = useMemo(
    () => (clientState ? getIanaTimezone(clientState) : undefined),
    [clientState],
  );
  const clientTzAbbrev = clientState
    ? getTimezoneAbbrev(clientState) || 'ET'
    : 'ET';

  // User's (viewer's) local timezone
  const userTzIana = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );
  const userTzAbbrev = useMemo(() => {
    const short = new Intl.DateTimeFormat('en-US', {
      timeZone: userTzIana,
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return short?.value || 'Local';
  }, [userTzIana]);

  // Are client and user in the same timezone?
  const sameTimezone = clientTzIana === userTzIana;

  const [calendarError, setCalendarError] = useState(false);

  // Form state for new/edit scheduling
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [primaryAssigneeId, setPrimaryAssigneeId] = useState<string | null>(
    null,
  );
  const [backupAssigneeId, setBackupAssigneeId] = useState<string | null>(
    null,
  );

  const { data: staffUsers = [], isLoading: staffLoading } =
    useGetStaffUsersQuery();

  const isScheduled = !!scheduledAt;

  // Prepare user lists with frequent assignees at top
  const frequentUserIds = new Set<string>();
  const frequentUsers: User[] = [];
  FREQUENT_ASSIGNEE_NAMES.forEach((name) => {
    const matched = staffUsers.find((user) => {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      return (
        fullName === name ||
        user.first_name === name ||
        fullName.includes(name) ||
        name.includes(fullName)
      );
    });
    if (matched && !frequentUserIds.has(matched.id)) {
      frequentUserIds.add(matched.id);
      frequentUsers.push(matched);
    }
  });

  const otherUsers = staffUsers
    .filter((u) => !frequentUserIds.has(u.id))
    .sort((a, b) => {
      const aName = `${a.first_name} ${a.last_name}`.trim() || a.username;
      const bName = `${b.first_name} ${b.last_name}`.trim() || b.username;
      return aName.localeCompare(bName);
    });

  const allUsers = [...frequentUsers, ...otherUsers];

  const handleStartEditing = (prefillFromExisting = true) => {
    setCalendarError(false);
    if (isScheduled && prefillFromExisting) {
      const d = new Date(scheduledAt);
      setSelectedDate(d);
      setSelectedTime(
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      );
      setPrimaryAssigneeId(scheduledAssigneeId);
      setBackupAssigneeId(scheduledBackupAssigneeId);
    } else {
      setSelectedDate(null);
      setSelectedTime('');
      setPrimaryAssigneeId(intakeAssignedTo);
      setBackupAssigneeId(null);
    }
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setCalendarError(false);
  };

  const canSubmit =
    selectedDate &&
    selectedTime &&
    primaryAssigneeId &&
    !isSubmitting;

  const buildPayload = (skipCalendar = false) => {
    if (!selectedDate || !selectedTime || !primaryAssigneeId)
      return null;

    // Build an ISO-ish string that the backend interprets in the client's
    // timezone (the backend passes the IANA tz to Google Calendar separately).
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const isoString = `${year}-${month}-${day}T${selectedTime}:00`;

    return {
      scheduled_at: isoString,
      scheduled_assignee_id: primaryAssigneeId,
      scheduled_backup_assignee_id: backupAssigneeId || undefined,
      skip_calendar: skipCalendar || undefined,
    };
  };

  const handleSubmit = async (skipCalendar = false) => {
    const payload = buildPayload(skipCalendar);
    if (!payload) return;

    setIsSubmitting(true);
    setCalendarError(false);
    try {
      await onSchedule(payload);
      setIsEditing(false);
      setCalendarError(false);
    } catch (err: unknown) {
      const apiError = err as { status?: number; data?: { calendar_failed?: boolean; error?: string } };
      if (apiError?.status === 422 && apiError?.data?.calendar_failed) {
        // Calendar-specific failure — show fallback UI
        setCalendarError(true);
      } else {
        // Any other error — still show calendar error so user can skip calendar
        setCalendarError(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSchedule = async () => {
    setIsSubmitting(true);
    try {
      await onCancel();
      setIsEditing(false);
      setIsExpanded(false);
      setCalendarError(false);
    } catch {
      // Error handling is done by the parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderUserDropdown = (
    label: string,
    value: string | null,
    onChange: (userId: string | null) => void,
  ) => {
    const selectedUser = value
      ? staffUsers.find((u) => u.id === value) || null
      : null;

    return (
      <div className="space-y-1">
        <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
          {label}
        </div>
        <ComboboxWithClear
          value={selectedUser}
          onChange={(user: User | null) => onChange(user?.id || null)}
          options={allUsers}
          displayValue={displayUserName}
          placeholder="Select staff member"
          className="w-full text-xs [&>span>input]:py-1.5 [&>span>input]:text-xs [&>span>input]:rounded-lg"
          aria-label={label}
          disabled={disabled || staffLoading || isSubmitting}
          clearable
          onClear={() => onChange(null)}
        >
          {(user: User) => {
            const isFrequent = frequentUserIds.has(user.id);
            const name = displayUserName(user);
            return (
              <ComboboxOption value={user} key={user.id}>
                <ComboboxLabel className={isFrequent ? 'font-medium' : ''}>
                  {name}
                  {isFrequent && (
                    <span className="ml-2 text-xs text-emerald-600 group-data-focus/option:text-white group-data-selected/option:text-white">
                      ★
                    </span>
                  )}
                </ComboboxLabel>
              </ComboboxOption>
            );
          }}
        </ComboboxWithClear>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-sm overflow-hidden transition-all">
      {/* Header */}
      <button
        type="button"
        onClick={() => {
          if (!isExpanded && !isScheduled) {
            handleStartEditing();
          } else {
            setIsExpanded(!isExpanded);
          }
        }}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-zinc-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <CalendarClock
            className={cn(
              'h-3.5 w-3.5 shrink-0',
              isScheduled ? 'text-emerald-600' : 'text-zinc-400',
            )}
          />
          <span className="text-xs font-semibold text-zinc-900">
            Scheduled Callback
          </span>
          {isScheduled && (
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-px text-[9px] font-bold text-emerald-700 uppercase tracking-wider">
              Scheduled
            </span>
          )}
        </div>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-zinc-400 transition-transform duration-200',
            !isExpanded && '-rotate-90',
          )}
        />
      </button>

      {isExpanded && (
        <div className="border-t border-zinc-100">
          {/* Display mode: show current schedule */}
          {isScheduled && !isEditing && (
            <div className="px-3 pb-2.5 space-y-2">
              {/* Schedule info card */}
              <div className="rounded-lg bg-zinc-50 px-2.5 py-2 space-y-1.5">
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className="text-sm font-bold text-zinc-900">
                    {clientTzIana
                      ? formatInTimeZone(new Date(scheduledAt), clientTzIana, 'MMM d, yyyy')
                      : format(new Date(scheduledAt), 'MMM d, yyyy')}
                  </span>
                  <span className="text-zinc-400 text-xs">at</span>
                  <span className="text-sm font-bold text-zinc-900">
                    {clientTzIana
                      ? formatInTimeZone(new Date(scheduledAt), clientTzIana, 'h:mm a')
                      : format(new Date(scheduledAt), 'h:mm a')}
                  </span>
                  <span className="text-[10px] font-semibold text-zinc-500">
                    {clientTzAbbrev}
                  </span>
                  {!sameTimezone && (
                    <span className="text-[11px] font-semibold text-zinc-500">
                      ({formatInTimeZone(new Date(scheduledAt), userTzIana, 'h:mm a')} {userTzAbbrev})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-zinc-600">
                  <div>
                    <span className="text-zinc-400 font-medium">Primary:</span>{' '}
                    {scheduledAssigneeName || 'Unknown'}
                  </div>
                  {scheduledBackupAssigneeName && (
                    <>
                      <div className="w-px h-3 bg-zinc-200" />
                      <div>
                        <span className="text-zinc-400 font-medium">Backup:</span>{' '}
                        {scheduledBackupAssigneeName}
                      </div>
                    </>
                  )}
                </div>

                {/* Google Calendar link */}
                {googleCalendarEventId && (
                  googleCalendarEventLink ? (
                    <a
                      href={googleCalendarEventLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                    >
                      <ExternalLink className="h-2.5 w-2.5" />
                      Google Calendar
                    </a>
                  ) : (
                    <div className="flex items-center gap-1 text-[11px] text-emerald-600">
                      <Check className="h-2.5 w-2.5" />
                      Calendar event created
                    </div>
                  )
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => handleStartEditing(true)}
                  disabled={disabled || isSubmitting}
                  className="h-7 px-2.5 rounded-full text-[11px] font-semibold bg-zinc-100 text-zinc-600 hover:bg-zinc-200 active:scale-95 transition-all flex items-center gap-1 disabled:opacity-50"
                >
                  <RotateCcw className="h-2.5 w-2.5" />
                  Reschedule
                </button>
<button
                  type="button"
                  onClick={handleCancelSchedule}
                  disabled={disabled || isSubmitting}
                  className="h-7 px-2 rounded-full text-[11px] font-semibold text-red-500 hover:bg-red-50 active:scale-95 transition-all flex items-center gap-0.5 disabled:opacity-50"
                >
                  <X className="h-2.5 w-2.5" />
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Edit / Create mode */}
          {(isEditing || (!isScheduled && isExpanded)) && (
            <div className="px-3 pb-2.5 space-y-2.5">
              {/* Calendar error fallback */}
              {calendarError && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-[11px] text-amber-800">
                      <p className="font-bold">
                        Calendar event could not be created.
                      </p>
                      <p className="mt-0.5 text-amber-700">
                        Schedule without calendar, or retry.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleSubmit(true)}
                      disabled={isSubmitting}
                      className="h-7 px-2.5 rounded-full text-[11px] font-bold bg-amber-600 text-white hover:bg-amber-700 active:scale-95 transition-all disabled:opacity-50"
                    >
                      {isSubmitting ? 'Saving...' : 'Skip Calendar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSubmit(false)}
                      disabled={isSubmitting}
                      className="h-7 px-2.5 rounded-full text-[11px] font-bold text-amber-700 hover:bg-amber-100 active:scale-95 transition-all disabled:opacity-50"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              )}

              {/* Date & Time row */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                    Date
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        disabled={disabled || isSubmitting}
                        className={cn(
                          'w-full h-8 px-2.5 rounded-lg border text-xs text-left transition-all flex items-center gap-1.5',
                          selectedDate
                            ? 'border-zinc-300 text-zinc-900 bg-white'
                            : 'border-zinc-200 text-zinc-400 bg-zinc-50',
                          'hover:border-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-200',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                        )}
                      >
                        <CalendarIcon className="h-3 w-3 text-zinc-400 shrink-0" />
                        {selectedDate
                          ? format(selectedDate, 'MMM d, yyyy')
                          : 'Pick a date'}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-lg" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate || undefined}
                        onSelect={(d) => setSelectedDate(d || null)}
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1">
                  <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                    Time
                  </div>
                  <select
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    disabled={disabled || isSubmitting}
                    className={cn(
                      'w-full h-8 rounded-lg border px-2 text-xs transition-all appearance-none',
                      selectedTime
                        ? 'border-zinc-300 text-zinc-900 bg-white'
                        : 'border-zinc-200 text-zinc-400 bg-zinc-50',
                      'hover:border-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-200',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                    )}
                  >
                    <option value="">Pick a time</option>
                    {TIME_SLOTS.map((slot) => {
                      const [val, label] = slot.split('|');
                      return (
                        <option key={val} value={val}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Timezone indicator */}
              <div className="flex items-center gap-1 text-[10px] text-zinc-400">
                <Globe className="h-2.5 w-2.5" />
                <span>
                  Times in <span className="font-semibold text-zinc-500">{clientTzAbbrev}</span>
                  {clientState ? ` (client: ${clientState})` : ''}
                  {!sameTimezone && clientTzIana && selectedDate && selectedTime && (() => {
                    // Show what the selected client-tz time is in the viewer's tz.
                    // Compute the offset difference between client tz and user tz.
                    const now = new Date();
                    const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC' });
                    const clientOff = new Date(now.toLocaleString('en-US', { timeZone: clientTzIana })).getTime() - new Date(utcStr).getTime();
                    const userOff = new Date(now.toLocaleString('en-US', { timeZone: userTzIana })).getTime() - new Date(utcStr).getTime();
                    // Parse the naive time as browser-local, then shift by the tz diff
                    const year = selectedDate.getFullYear();
                    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                    const day = String(selectedDate.getDate()).padStart(2, '0');
                    const asLocal = new Date(`${year}-${month}-${day}T${selectedTime}:00`);
                    const shifted = new Date(asLocal.getTime() + (userOff - clientOff));
                    const converted = shifted.toLocaleTimeString('en-US', {
                      hour: 'numeric', minute: '2-digit', hour12: true,
                    });
                    return (
                      <span className="text-zinc-500">
                        {' · '}{converted} <span className="font-semibold">{userTzAbbrev}</span> your time
                      </span>
                    );
                  })()}
                </span>
              </div>

              {/* Primary Assignee */}
              {renderUserDropdown(
                'Primary Assignee',
                primaryAssigneeId,
                setPrimaryAssigneeId,
              )}

              {/* Backup Assignee */}
              {renderUserDropdown(
                'Backup Assignee',
                backupAssigneeId,
                setBackupAssigneeId,
              )}

              {/* Action buttons */}
              {!calendarError && (
                <div className="flex items-center gap-1.5 pt-0.5">
                  <button
                    type="button"
                    onClick={() => handleSubmit(false)}
                    disabled={!canSubmit}
                    className={cn(
                      'h-8 px-3.5 rounded-full text-[11px] font-bold transition-all flex items-center gap-1.5',
                      canSubmit
                        ? 'bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95 shadow-sm'
                        : 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
                    )}
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <CalendarClock className="h-3 w-3" />
                    )}
                    {isSubmitting
                      ? 'Scheduling...'
                      : isScheduled
                        ? 'Update'
                        : 'Schedule'}
                  </button>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isSubmitting}
                      className="h-8 px-3 rounded-full text-[11px] font-bold text-zinc-500 hover:bg-zinc-100 active:scale-95 transition-all disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ScheduleCallbackPanel = React.memo(ScheduleCallbackPanelComponent);
